// ============================================================================
// CONFIG SERVICE
// ============================================================================
// HTTP client wrapper around api/v1/Config (ConfigController).
//
// No memory caching: every effective-value request hits the backend.
// Modal/screen consumers should refetch on open to ensure freshness.

import { get, put } from '../client/axiosClient';
import { configEndpoints } from '../endpoints';
import type {
  ApiResponse,
  ConfigDefinition,
  ConfigEffectiveValue,
  ConfigGroup,
  ConfigScopeRef,
  ConfigScopeRow,
  UpdateConfigRequest,
} from '../types';

/**
 * Build the comma-delimited "scopes" query parameter from an array of scope refs.
 *   [{ type: 'OFFICE', id: 'abc' }, { type: 'EMPLOYEE', id: 'xyz' }]
 *   → "OFFICE:abc,EMPLOYEE:xyz"
 *
 * SYSTEM is always implicit on the backend, but passing it is harmless.
 */
function buildScopesParam(scopes?: ConfigScopeRef[]): string | undefined {
  if (!scopes || scopes.length === 0) return undefined;
  const parts = scopes
    .filter(s => s.type === 'SYSTEM' || (!!s.id && s.id !== ''))
    .map(s => (s.type === 'SYSTEM' ? 'SYSTEM:' : `${s.type}:${s.id}`));
  return parts.length > 0 ? parts.join(',') : undefined;
}

class ConfigService {
  async getGroups(): Promise<ApiResponse<ConfigGroup[]>> {
    const response = await get<ApiResponse<ConfigGroup[]>>(configEndpoints.groups());
    return response.data;
  }

  async getDefinitions(group: string): Promise<ApiResponse<ConfigDefinition[]>> {
    const response = await get<ApiResponse<ConfigDefinition[]>>(configEndpoints.definitions(group));
    return response.data;
  }

  async getEffectiveConfig(
    group: string,
    scopes?: ConfigScopeRef[]
  ): Promise<ApiResponse<ConfigEffectiveValue[]>> {
    const scopesParam = buildScopesParam(scopes);
    const response = await get<ApiResponse<ConfigEffectiveValue[]>>(
      configEndpoints.effective(group, scopesParam)
    );
    return response.data;
  }

  async getEffectiveValue(
    configKey: string,
    scopes?: ConfigScopeRef[]
  ): Promise<ApiResponse<{ configKey: string; effectiveValue: string }>> {
    const scopesParam = buildScopesParam(scopes);
    const response = await get<ApiResponse<{ configKey: string; effectiveValue: string }>>(
      configEndpoints.effectiveByKey(configKey, scopesParam)
    );
    return response.data;
  }

  async getScopes(scopeType: string): Promise<ApiResponse<ConfigScopeRow[]>> {
    const response = await get<ApiResponse<ConfigScopeRow[]>>(configEndpoints.scopes(scopeType));
    return response.data;
  }

  async updateValues(request: UpdateConfigRequest): Promise<ApiResponse<{ updated: number }>> {
    const response = await put<ApiResponse<{ updated: number }>>(
      configEndpoints.updateValues(),
      request
    );
    return response.data;
  }
}

export const configService = new ConfigService();
export default configService;
